import React, { useState } from "react";
import {
  GitBranch,
  Landmark,
  BarChart3,
  Building2,
  CircleCheck,
  Banknote,
  Users,
} from "lucide-react";
import { useFunderDashboardQuery } from "@/hooks/queries";
import { usePersistentTab } from "@/hooks/usePersistentTab";
import DealPipeline from "@/components/dashboard/DealPipeline";
import PortfolioOverview from "@/components/dashboard/PortfolioOverview";
import AnalyticsCharts from "@/components/dashboard/AnalyticsCharts";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  isLoading: boolean;
}> = ({ title, value, icon, color, isLoading }) => (
  <Card className="h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
    <CardContent className="p-5">
      <div className="mb-3 flex items-center">
        <div
          className="mr-3 flex h-12 w-12 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}26`, color }}
        >
          {icon}
        </div>
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
      </div>
      {isLoading ? (
        <Skeleton className="h-11 w-24" />
      ) : (
        <p className="text-3xl font-bold tracking-tight">{value}</p>
      )}
    </CardContent>
  </Card>
);

const DASHBOARD_TAB_KEYS = ["pipeline", "portfolio", "analytics"] as const;
type DashboardTabKey = (typeof DASHBOARD_TAB_KEYS)[number];

const Dashboard: React.FC = () => {
  const [period, setPeriod] = useState<number>(30);
  const [activeTab, setActiveTab] = usePersistentTab<DashboardTabKey>(
    "funder_dashboard_active_tab_v2",
    "main",
    "pipeline",
    DASHBOARD_TAB_KEYS
  );
  const { data, isLoading } = useFunderDashboardQuery(period);

  const kpis = data?.kpis;
  const pipeline = data?.pipeline ?? [];
  const portfolio = data?.portfolio ?? {
    activeRepayments: 0,
    totalFunded: 0,
    dailyCollections: 0,
    weeklyCollections: 0,
    avgFactorRate: 0,
    avgLeverage: 0,
    repaymentDeals: [],
  };
  const analytics = data?.analytics ?? {
    total_decisions: 0,
    approved_count: 0,
    declined_count: 0,
    approval_percentage: 0,
    decline_percentage: 0,
    avg_deal_size: 0,
    avg_decision_time: 0,
    top_decline_reasons: [],
    period_days: period,
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Funder Dashboard</h1>
        <div className="flex flex-col gap-2 sm:w-[160px]">
          <Label className="sr-only">Period</Label>
          <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
            <SelectTrigger>
              <SelectValue placeholder="Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard
          title="Pipeline"
          value={kpis?.totalPipeline ?? 0}
          icon={<GitBranch className="h-6 w-6" />}
          color="#1976d2"
          isLoading={isLoading}
        />
        <StatCard
          title="Active Deals"
          value={kpis?.activeDeals ?? 0}
          icon={<Building2 className="h-6 w-6" />}
          color="#9c27b0"
          isLoading={isLoading}
        />
        <StatCard
          title="Approval Rate"
          value={`${kpis?.approvalRate ?? 0}%`}
          icon={<CircleCheck className="h-6 w-6" />}
          color="#2e7d32"
          isLoading={isLoading}
        />
        <StatCard
          title="Total Volume"
          value={formatCurrency(kpis?.totalVolume ?? 0)}
          icon={<Banknote className="h-6 w-6" />}
          color="#ed6c02"
          isLoading={isLoading}
        />
        <StatCard
          title="Brokers"
          value={kpis?.totalBrokers ?? 0}
          icon={<Users className="h-6 w-6" />}
          color="#0288d1"
          isLoading={isLoading}
        />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as DashboardTabKey)}
        className="mb-4"
      >
        <TabsList className="mb-4 h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
          <TabsTrigger value="pipeline" className="gap-2 data-[state=active]:text-foreground">
            <GitBranch className="h-4 w-4" />
            Deal Pipeline
          </TabsTrigger>
          <TabsTrigger value="portfolio" className="gap-2 data-[state=active]:text-foreground">
            <Landmark className="h-4 w-4" />
            Portfolio
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2 data-[state=active]:text-foreground">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pipeline" className="mt-0">
          <DealPipeline pipeline={pipeline} isLoading={isLoading} />
        </TabsContent>
        <TabsContent value="portfolio" className="mt-0">
          <PortfolioOverview
            portfolio={portfolio}
            leverageDistribution={analytics.leverage_distribution}
            isLoading={isLoading}
          />
        </TabsContent>
        <TabsContent value="analytics" className="mt-0">
          <AnalyticsCharts analytics={analytics} period={period} isLoading={isLoading} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
